import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase/config';
import './FeedbackForm.css';

const FeedbackForm = () => {
  const [formData, setFormData] = useState({
    type: 'suggestion',
    title: '',
    content: ''
  });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 3) {
      alert('이미지는 최대 3개까지 첨부 가능합니다.');
      return;
    }

    setImages(prev => [...prev, ...files]);

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    const imageUrls = [];
    for (const image of images) {
      const imageRef = ref(storage, `feedback/${Date.now()}_${image.name}`);
      await uploadBytes(imageRef, image);
      const url = await getDownloadURL(imageRef);
      imageUrls.push(url);
    }
    return imageUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.content.trim()) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      const imageUrls = images.length > 0 ? await uploadImages() : [];
      const currentUser = auth.currentUser;

      await addDoc(collection(db, 'feedbacks'), {
        ...formData,
        images: imageUrls,
        authorId: currentUser?.uid || 'anonymous',
        authorEmail: currentUser?.email || '익명',
        authorName: currentUser?.displayName || currentUser?.email || '익명',
        status: 'pending',
        isRead: false,
        createdAt: serverTimestamp()
      });

      setSuccess(true);
      setFormData({
        type: 'suggestion',
        title: '',
        content: ''
      });
      setImages([]);
      setImagePreviews([]);

      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('피드백 전송 실패:', error);
      alert('피드백 전송에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="feedback-form-container">
      <div className="feedback-form-header">
        <h2>피드백 보내기</h2>
        <p>DentConnect를 더 나은 서비스로 만들기 위해 여러분의 의견을 들려주세요.</p>
        <div className="privacy-notice">
          💡 제출하신 피드백은 관리자가 검토하며, 서비스 개선에 활용됩니다.
        </div>
      </div>

      {success && (
        <div className="success-message">
          ✅ 피드백이 성공적으로 전송되었습니다. 소중한 의견 감사합니다!
        </div>
      )}

      <form className="feedback-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="type">피드백 유형 *</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="form-select"
            required
          >
            <option value="suggestion">💡 제안</option>
            <option value="bug">🐛 버그 제보</option>
            <option value="improvement">⚡ 개선 요청</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="title">제목 *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="form-input"
            placeholder="피드백 제목을 입력하세요"
            maxLength={100}
            required
          />
          <small>{formData.title.length}/100</small>
        </div>

        <div className="form-group">
          <label htmlFor="content">내용 *</label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleChange}
            className="form-textarea"
            placeholder="자세한 내용을 입력해주세요. 버그인 경우 발생 상황을 구체적으로 설명해주시면 도움이 됩니다."
            rows={8}
            maxLength={1000}
            required
          />
          <small>{formData.content.length}/1000</small>
        </div>

        <div className="form-group">
          <label htmlFor="images">이미지 첨부 (선택)</label>
          <input
            type="file"
            id="images"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            className="form-file"
            disabled={images.length >= 3}
          />
          <small>최대 3개까지 첨부 가능 (각 5MB 이하)</small>

          {imagePreviews.length > 0 && (
            <div className="image-preview-list">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="image-preview-item">
                  <img src={preview} alt={`preview-${index}`} />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeImage(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          type="submit"
          className="submit-btn"
          disabled={loading}
        >
          {loading ? '전송 중...' : '피드백 제출'}
        </button>
      </form>
    </div>
  );
};

export default FeedbackForm;